import { registerDecorators as _registerDecorators, registerComponent as _registerComponent, LightningElement } from "lwc";
import _tmpl from "./securityHeader.html";
import { WireStore } from '@oneappexchange/lwc-wired';
import equalityFn from 'zustand/shallow';
import { WireI18n, i18nStore } from '~/modules/stores/i18nStore';
import { SOLUTIONS_ROUTE, navigate } from '~/modules/stores/routerStore';
import { securityReviewPaymentStore } from '~/modules/stores/securityReviewPaymentStore';
import { securityReviewStore, securityReviewStoreValidator } from '~/modules/stores/securityReviewStore';
import { images } from '~/modules/utilities/assetUtilities';
import { TECH_SOLUTION_TYPE_ICON_MAP, isApiSolutionType } from '~/modules/utilities/schemaUtilities';
import { OpsSecurityOpsReviewStatus, PartnerSecurityReviewStatus } from '~/modules/utilities/securityReviewUtilities';
import { isEditable, selectSolutionName } from '~/modules/utilities/solutionUtilities';
const HEADER_SCROLL_Y_111 = 111;
const HEADER_SCROLL_Y_90 = 90;
class SecurityHeader extends LightningElement {
  constructor(...args) {
    super(...args);
    this.images = images;
    this.showMaintenanceBanner = void 0;
    this.showSaving = false;
    this.showSaved = true;
    this.shrinkHeader = false;
    this.securityReview = void 0;
    this.solution = void 0;
    this.securityReviewOps = void 0;
    this.isLoading = void 0;
    this.isSaving = void 0;
    this.uploadingAssets = void 0;
    this.percentageCompleted = 0;
    this.labels = {};
    this.solutionsTypesLabels = {};
    this.sharedLabels = {};
    this.titleLabel = void 0;
    this.subtitleLabel = void 0;
    this.failed = void 0;
    this.pass = void 0;
    this.paymentRequired = void 0;
    this.paymentCompleted = void 0;
    this.STATUS_DATA = {
      [OpsSecurityOpsReviewStatus.failedTest]: labels => ({
        title: labels.statusTitleFailed,
        subtitle: labels.statusSubtitleFailed,
        pass: false,
        percentage: 100,
        failed: true
      }),
      [OpsSecurityOpsReviewStatus.expired]: labels => ({
        title: labels.statusTitleExpired,
        subtitle: labels.statusSubtitleExpired,
        pass: false,
        percentage: 100,
        failed: true
      }),
      [OpsSecurityOpsReviewStatus.pass]: labels => ({
        title: labels.statusTitlePassed,
        subtitle: labels.statusSubtitlePassed,
        pass: true,
        percentage: 100,
        failed: false
      }),
      [OpsSecurityOpsReviewStatus.returnedOps]: labels => ({
        title: labels.statusTitleReturned,
        subtitle: labels.statusSubtitleReturned,
        pass: false,
        percentage: 100,
        failed: false
      }),
      [OpsSecurityOpsReviewStatus.returnedProdSec]: labels => ({
        title: labels.statusTitleReturned,
        subtitle: labels.statusSubtitleReturned,
        pass: false,
        percentage: 100,
        failed: false
      })
    };
    this.updateHeader = () => {
      let posY = window.scrollY;
      const staticProgress = this.template.querySelector('.static-progress');
      const floatingProgress = this.template.querySelector('.floating-progress');
      const listingIcon = this.template.querySelector('.review-icon');
      if (posY >= HEADER_SCROLL_Y_111) {
        if (staticProgress) {
          staticProgress.classList.remove('hidden');
        }
        if (floatingProgress) {
          floatingProgress.classList.add('hidden');
        }
        if (listingIcon) {
          listingIcon.style.transform = 'translate(-111px, 0px)';
        }
      } else {
        if (staticProgress) {
          staticProgress.classList.add('hidden');
        }
        if (floatingProgress) {
          floatingProgress.classList.remove('hidden');
        }
        if (listingIcon) {
          if (posY > HEADER_SCROLL_Y_90) {
            posY = HEADER_SCROLL_Y_111;
          }
          posY = posY * -1;
          listingIcon.style.transform = 'translate(' + posY + 'px, 0px)';
        }
      }
    };
  }
  i18nCallback(i18n) {
    this.labels = i18n.reviewEditorHeader;
    this.sharedLabels = i18n.shared;
    this.solutionsTypesLabels = i18n.technologiesPage.solutionsTypes;
  }
  securityReviewStoreCallback({
    isLoading,
    isSaving,
    securityReview,
    securityReviewOps,
    uploadingAssets,
    solution
  }) {
    this.isLoading = isLoading;
    this.isSaving = isSaving;
    this.securityReview = securityReview;
    this.securityReviewOps = securityReviewOps;
    this.uploadingAssets = uploadingAssets;
    this.solution = solution;
    this.updatePercentageAndLabels();
  }
  securityReviewPaymentStoreCallback({
    paymentRequired,
    paymentCompleted
  }) {
    this.paymentRequired = paymentRequired;
    this.paymentCompleted = paymentCompleted;
    this.updatePercentageAndLabels();
  }
  get isReadOnly() {
    if (this.securityReviewOps && this.securityReview) {
      return !isEditable(this.securityReview.status, this.securityReviewOps.status);
    }
    return false;
  }
  connectedCallback() {
    window.addEventListener('scroll', this.updateHeader, false);
    this.updateHeader();
  }
  disconnectedCallback() {
    window.removeEventListener('scroll', this.updateHeader);
  }
  handleSaveAndExit(event) {
    event.preventDefault();
    if (this.isReadOnly) {
      navigate(SOLUTIONS_ROUTE);
    } else {
      securityReviewStore.saveSecurityReview();
    }
  }
  get iconTypeClasses() {
    return 'icon-type ' + this.solution.technology;
  }
  get wayfinderClasses() {
    if (this.showMaintenanceBanner) {
      return 'wayfinder ' + this.solution.technology + ' wayfinder-maintenance';
    }
    return 'wayfinder ' + this.solution.technology;
  }
  get saveButton() {
    return {
      onclick: this.handleSaveAndExit,
      label: this.saveButtonLabel,
      iconName: this.isSaving ? 'utility:spinner' : undefined,
      variant: 'neutral',
      class: 'small-button',
      disabled: this.isSaveDisabled
    };
  }
  get isSaveDisabled() {
    return this.isSaving || this.uploadingAssets > 0;
  }
  get saveButtonLabel() {
    if (this.isReadOnly) {
      return this.labels.reviewExit;
    }
    return this.isSaving ? this.sharedLabels.saving : this.labels.reviewSaveAndExit;
  }
  get solutionName() {
    return this.solution ? this.processSolutionName(this.solution) : '';
  }
  get solutionSubName() {
    if (!isApiSolutionType(this.solution.technology)) {
      return i18nStore.getState().i18n.reviewEditorHeader.securityReviewSubName({
        VERSION: selectSolutionName(this.solution)
      });
    }
    return '';
  }
  get solutionType() {
    return this.solution && isApiSolutionType(this.solution.technology) ? this.solutionsTypesLabels.api : this.solutionsTypesLabels.package;
  }
  get iconTemplate() {
    return TECH_SOLUTION_TYPE_ICON_MAP[this.solution.technology];
  }
  get isSubmitted() {
    return this.securityReview.isSubmitted || false;
  }
  processSolutionName(solution) {
    if (!isApiSolutionType(solution.technology)) {
      return solution.solution.name;
    }
    return solution.name;
  }
  updatePercentageAndLabels() {
    if (!this.securityReview) {
      this.percentageCompleted = 0;
      return;
    }

    //default title and subtitle text
    this.titleLabel = this.labels.statusTitleStartYourSubmission;
    this.subtitleLabel = this.labels.statusSubtitleStartYourSubmission;
    this.failed = false;
    this.pass = false;
    this.percentageCompleted = 0;
    const srStatus = this.securityReview.status;
    const opsStatus = this.securityReviewOps.status;
    if (this.STATUS_DATA[opsStatus]) {
      const status = this.STATUS_DATA[opsStatus](this.labels);
      this.titleLabel = status.title;
      this.subtitleLabel = status.subtitle;
      this.failed = status.failed;
      this.pass = status.pass;
      this.percentageCompleted = status.percentage;
      return;
    } else if (srStatus === PartnerSecurityReviewStatus.submitted || srStatus === PartnerSecurityReviewStatus.reSubmitted) {
      this.percentageCompleted = 100;
      this.titleLabel = this.labels.statusTitleSubmitted;
      this.subtitleLabel = this.labels.statusSubtitleSubmitted;
      return;
    }
    this.getPercentageByStepsCompleted();
  }
  getPercentageByStepsCompleted() {
    const {
      stepsCompleted,
      numberOfSteps,
      numberOfStepsWithFields
    } = securityReviewStoreValidator;
    if (stepsCompleted.length === numberOfStepsWithFields) {
      if (!this.paymentRequired || this.paymentCompleted) {
        this.percentageCompleted = 95;
        this.titleLabel = this.labels.statusTitleOnYourWay;
        this.subtitleLabel = this.labels.statusSubtitleOnYourWay;
        return;
      }
    }
    if (stepsCompleted.length > 0 && numberOfSteps) {
      const completed = Math.floor(stepsCompleted.length * 100 / numberOfSteps);
      this.percentageCompleted = completed;
      this.titleLabel = this.labels.statusTitleOnYourWay;
      this.subtitleLabel = this.labels.statusSubtitleOnYourWay;
    }
  }
  /*LWC compiler v7.1.3*/
}
SecurityHeader.delegatesFocus = true;
_registerDecorators(SecurityHeader, {
  publicProps: {
    showMaintenanceBanner: {
      config: 0
    }
  },
  wire: {
    i18nCallback: {
      adapter: WireI18n,
      method: 1,
      config: function ($cmp) {
        return {};
      }
    },
    securityReviewStoreCallback: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: securityReviewStore,
          selector: ({
            isLoading,
            isSaving,
            securityReview,
            securityReviewOps,
            uploadingAssets,
            solution
          }) => ({
            isLoading,
            isSaving,
            securityReview,
            securityReviewOps,
            uploadingAssets,
            solution
          }),
          equalityFn
        };
      }
    },
    securityReviewPaymentStoreCallback: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: securityReviewPaymentStore,
          selector: ({
            paymentRequired,
            paymentCompleted
          }) => ({
            paymentRequired,
            paymentCompleted
          }),
          equalityFn
        };
      }
    }
  },
  fields: ["images", "showSaving", "showSaved", "shrinkHeader", "securityReview", "solution", "securityReviewOps", "isLoading", "isSaving", "uploadingAssets", "percentageCompleted", "labels", "solutionsTypesLabels", "sharedLabels", "titleLabel", "subtitleLabel", "failed", "pass", "paymentRequired", "paymentCompleted", "STATUS_DATA", "updateHeader"]
});
const __lwc_component_class_internal = _registerComponent(SecurityHeader, {
  tmpl: _tmpl,
  sel: "appx-security-header",
  apiVersion: 62
});
export default __lwc_component_class_internal;